/**
 * themeEngine — resolves a tenant's theme config ({preset, overrides, darkMode}
 * from app-properties.json) into brand CSS custom properties, applies them to
 * <html>, and owns light/dark mode switching.
 *
 * Static semantic tokens live in tokens.css; this module only computes the
 * brand-derived ramp (tint/shade done in JS — no CSS color-mix(), which old
 * SEB/Chromium exam browsers lack).
 *
 * Boot flow: public/index.html has an inline script that applies the cached
 * resolved theme (localStorage "themeCache:<tenant>") pre-paint; once the real
 * config arrives, apply() recomputes, refreshes the cache and dispatches a
 * window "themechange" event (charts re-read their palette on it).
 *
 * NOTE: keep in sync with ilearn/src/theme/themeEngine.js (hand-synced twin).
 */
import Config from "../_config/config";
import { getPresetById } from "./presets.generated";

export type ThemeMode = "light" | "dark";

export interface TenantThemeConfig {
  preset?: string;
  overrides?: { primary?: string; accent?: string };
  darkMode?: { enabled?: boolean; default?: "light" | "dark" | "system" };
  version?: number;
}

interface ResolvedTheme {
  brandVarsLight: Record<string, string>;
  brandVarsDark: Record<string, string>;
  darkEnabled: boolean;
  darkDefault: "light" | "dark" | "system";
  metaLight: string;
  metaDark: string;
}

const APP = "iteach" as const;
const CACHE_VERSION = 1;
const MODE_KEY = "themeMode";
const DARK_SURFACE = "#1a2130"; // must match --surface-card dark in tokens.css
const META_DARK = "#0f1420"; // must match --surface-page dark in tokens.css

// ---------------- color math ----------------
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** mix `weight` of colorA into colorB (weight 0..1) */
function mix(colorA: string, colorB: string, weight: number): string {
  const [r1, g1, b1] = hexToRgb(colorA);
  const [r2, g2, b2] = hexToRgb(colorB);
  const w = Math.max(0, Math.min(1, weight));
  return rgbToHex(
    r1 * w + r2 * (1 - w),
    g1 * w + g2 * (1 - w),
    b1 * w + b2 * (1 - w)
  );
}

const tint = (hex: string, amt: number) => mix("#ffffff", hex, amt);
const shade = (hex: string, amt: number) => mix("#000000", hex, amt);

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((n) => {
    const s = n / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const rgbTriplet = (hex: string) => hexToRgb(hex).join(", ");

// ---------------- brand ramp ----------------
function computeBrandVars(
  primary: string,
  accent: string,
  mode: ThemeMode
): Record<string, string> {
  const dark = mode === "dark";
  const vars: Record<string, string> = {
    "--brand-primary": primary,
    "--brand-primary-rgb": rgbTriplet(primary),
    "--brand-primary-hover": dark ? tint(primary, 0.12) : shade(primary, 0.08),
    "--brand-primary-active": dark ? tint(primary, 0.2) : shade(primary, 0.14),
    "--brand-primary-subtle": dark
      ? mix(primary, DARK_SURFACE, 0.18)
      : tint(primary, 0.9),
    "--brand-accent": accent,
    "--brand-accent-rgb": rgbTriplet(accent),
    "--brand-accent-hover": dark ? tint(accent, 0.12) : shade(accent, 0.08),
    "--brand-accent-subtle": dark
      ? mix(accent, DARK_SURFACE, 0.18)
      : tint(accent, 0.92),
    "--brand-ink": dark
      ? "#f1f5f9"
      : luminance(primary) < 0.08
        ? primary
        : shade(primary, 0.4),
    "--gradient-brand": `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
    "--gradient-brand-soft": dark
      ? `linear-gradient(135deg, ${mix(primary, DARK_SURFACE, 0.18)} 0%, ${mix(accent, DARK_SURFACE, 0.18)} 100%)`
      : `linear-gradient(135deg, ${tint(primary, 0.9)} 0%, ${tint(accent, 0.92)} 100%)`,
    "--focus-ring-color": dark ? tint(primary, 0.15) : primary,
    // very dark primaries (navy) are unreadable as link text — fall back to accent
    "--text-link": dark
      ? tint(primary, 0.2)
      : luminance(primary) < 0.05
        ? accent
        : primary,
  };
  return vars;
}

// ---------------- resolution ----------------
const validHex = (v: unknown): v is string =>
  typeof v === "string" && HEX_RE.test(v);

export function resolveTheme(cfg?: TenantThemeConfig | null): ResolvedTheme {
  const preset = getPresetById(cfg?.preset, APP, Config.site);
  const primary = validHex(cfg?.overrides?.primary)
    ? cfg!.overrides!.primary!.toLowerCase()
    : preset.brand.primary.toLowerCase();
  const accent = validHex(cfg?.overrides?.accent)
    ? cfg!.overrides!.accent!.toLowerCase()
    : preset.brand.accent.toLowerCase();
  const overridden =
    primary !== preset.brand.primary.toLowerCase() ||
    accent !== preset.brand.accent.toLowerCase();

  // dark brand: preset-authored values unless the tenant overrode the colors,
  // in which case derive readable-on-dark variants from the overrides
  const darkPrimary =
    !overridden && preset.dark?.primary
      ? preset.dark.primary.toLowerCase()
      : tint(primary, 0.35);
  const darkAccent =
    !overridden && preset.dark?.accent
      ? preset.dark.accent.toLowerCase()
      : tint(accent, 0.35);

  const t = preset.tokenOverrides || {};
  return {
    brandVarsLight: { ...computeBrandVars(primary, accent, "light"), ...(t.light || {}) },
    brandVarsDark: { ...computeBrandVars(darkPrimary, darkAccent, "dark"), ...(t.dark || {}) },
    darkEnabled: cfg?.darkMode?.enabled === true,
    darkDefault: cfg?.darkMode?.default || "system",
    metaLight: primary,
    metaDark: META_DARK,
  };
}

// ---------------- application ----------------
let current: ResolvedTheme | null = null;
let appliedKeys: string[] = [];
let lightLocks = 0;

const root = () => document.documentElement;

function storedMode(): ThemeMode | null {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === "dark" || m === "light" ? m : null;
  } catch {
    return null;
  }
}

function desiredMode(resolved: ResolvedTheme): ThemeMode {
  if (!resolved.darkEnabled || lightLocks > 0) return "light";
  const stored = storedMode();
  if (stored) return stored;
  if (resolved.darkDefault === "dark") return "dark";
  if (resolved.darkDefault === "light") return "light";
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function setVars(vars: Record<string, string>) {
  const style = root().style;
  appliedKeys.forEach((k) => style.removeProperty(k));
  Object.entries(vars).forEach(([k, v]) => style.setProperty(k, v));
  appliedKeys = Object.keys(vars);
}

function setMeta(content: string) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", content);
}

function render(mode: ThemeMode) {
  if (!current) return;
  root().setAttribute("data-theme", mode);
  setVars(mode === "dark" ? current.brandVarsDark : current.brandVarsLight);
  setMeta(mode === "dark" ? current.metaDark : current.metaLight);
  document.getElementById("theme-boot")?.remove();
  window.dispatchEvent(new CustomEvent("themechange", { detail: { mode } }));
}

function writeCache(resolved: ResolvedTheme) {
  try {
    localStorage.setItem(
      `themeCache:${Config.site}`,
      JSON.stringify({ v: CACHE_VERSION, ...resolved })
    );
  } catch {
    /* storage full/unavailable — boot just falls back to bundled defaults */
  }
}

/** Apply the tenant's theme config (from app-properties.json). */
export function applyTheme(cfg?: TenantThemeConfig | null) {
  current = resolveTheme(cfg);
  writeCache(current);
  render(desiredMode(current));
}

/**
 * Apply bundled defaults at boot. No-op when the pre-paint boot script already
 * restored a cached theme (don't stomp it with defaults, and don't poison the
 * cache on routes that never fetch app-properties).
 */
export function applyDefaultTheme() {
  if (document.getElementById("theme-boot")) return;
  current = resolveTheme(null);
  render(desiredMode(current));
}

export function getMode(): ThemeMode {
  return root().getAttribute("data-theme") === "dark" ? "dark" : "light";
}

export function isDarkModeAvailable(): boolean {
  return !!current?.darkEnabled;
}

/** User toggle — persists the choice and re-renders. */
export function setMode(mode: ThemeMode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* non-fatal */
  }
  if (current) render(desiredMode(current));
}

/** Force light on exam/print/receipt surfaces. Balanced with unlockLight(). */
export function lockLight() {
  lightLocks++;
  if (current) render("light");
  else root().setAttribute("data-theme", "light");
}

export function unlockLight() {
  lightLocks = Math.max(0, lightLocks - 1);
  if (lightLocks === 0 && current) render(desiredMode(current));
}

// Follow OS scheme changes while the tenant default is "system" and the user
// hasn't made an explicit choice.
if (typeof window !== "undefined" && window.matchMedia) {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (current && current.darkDefault === "system" && !storedMode()) {
      render(desiredMode(current));
    }
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else if ((mq as any).addListener) (mq as any).addListener(onChange);
}
